import { useContext, useState } from "react";
import { useEffect } from "react";
import CiroModal from "../shared/CiroModal";
import classNames from "classnames";
import { gql, useMutation } from "@apollo/client";
import CiroButton, { CiroButtonStyleEnum } from "../shared/CiroButton";
import {
  AutopilotExpandSearchModal_GenerateExpandPromptsMutation,
  AutopilotExpandSearchModal_GenerateExpandPromptsMutationVariables,
} from "../../__generated__/graphql";
import CiroSpinner from "../shared/CiroSpinner";
import InternalAutopilotHydrationContext from "../../contexts/InternalAutopilotHydrationContext";
import { useNavigate } from "react-router-dom";
import CiroErrorMsg from "../shared/forms/CiroErrorMsg";
import { AutopilotCuratedInternalSearchContext_InternalSearchFilters } from "../../contexts/AutopilotCuratedInternalSearchContext";
import { AutopilotCuratedLinkedinSalesNavigatorSearchContext_LinkedinSalesNavigatorFilters } from "../../contexts/AutopilotCuratedLinkedinSalesNavigatorSearchContext";
import LsnAutopilotHydrationContext from "../../contexts/LsnAutopilotHydrationContext";
import CiroTextArea from "../shared/CiroTextArea";

const AutopilotExpandSearchModal_GenerateExpandPrompts = gql`
  mutation AutopilotExpandSearchModal_GenerateExpandPrompts(
    $curatedSearchId: Int!
  ) {
    generateExpandPrompts(curatedSearchId: $curatedSearchId) {
      expandPrompts
      curatedSearch {
        id
        scoringCriteria {
          criterion
        }
        internalCuratedSearch {
          filters {
            ...AutopilotCuratedInternalSearchContext_InternalSearchFilters
          }
        }
        linkedinSalesNavigatorCuratedSearch {
          filters {
            ...AutopilotCuratedLinkedinSalesNavigatorSearchContext_LinkedinSalesNavigatorFilters
          }
        }
        prompt
      }
    }
  }
  ${AutopilotCuratedInternalSearchContext_InternalSearchFilters}
  ${AutopilotCuratedLinkedinSalesNavigatorSearchContext_LinkedinSalesNavigatorFilters}
`;

const ExpandSearchModalOption = ({
  description,
  selected,
  onClick,
}: {
  description: string;
  selected: boolean;
  onClick: () => void;
}) => {
  return (
    <div
      className={classNames(
        "p-4",
        "border-1",
        "border-neutral-200",
        "bg-neutral-100",
        "rounded-lg",
        "cursor-pointer",
        "hover:bg-neutral-200",
        {
          "border-orange-500": selected,
        },
      )}
      onClick={onClick}
    >
      <div>{description}</div>
    </div>
  );
};

interface IAutopilotExpandSearchModal {
  onClose: () => void;
  curatedSearchId: number | null;
}

const AutopilotExpandSearchModal = ({
  onClose,
  curatedSearchId,
}: IAutopilotExpandSearchModal) => {
  const navigate = useNavigate();

  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const { resetHydrationValues: resetInternalHydrationValues } = useContext(
    InternalAutopilotHydrationContext,
  );
  const { resetHydrationValues: resetLsnHydrationValues } = useContext(
    LsnAutopilotHydrationContext,
  );
  const [error, setError] = useState<string | null>(null);
  const [customExpandPrompt, setCustomExpandPrompt] = useState<string>("");

  const [
    generateExpandPrompts,
    { loading: expandPromptsLoading, data: expandPromptsData },
  ] = useMutation<
    AutopilotExpandSearchModal_GenerateExpandPromptsMutation,
    AutopilotExpandSearchModal_GenerateExpandPromptsMutationVariables
  >(AutopilotExpandSearchModal_GenerateExpandPrompts);

  useEffect(() => {
    if (curatedSearchId) {
      generateExpandPrompts({
        variables: {
          curatedSearchId,
        },
      });
    } else {
      setSelectedOption(null);
      setCustomExpandPrompt("");
    }
  }, [curatedSearchId, generateExpandPrompts]);
  const handleSubmit = (selectedOption: string) => {
    if (
      expandPromptsData?.generateExpandPrompts?.curatedSearch
        ?.internalCuratedSearch
    ) {
      resetInternalHydrationValues({
        prompt:
          expandPromptsData?.generateExpandPrompts.curatedSearch.prompt ?? "",
        expandPrompt: selectedOption,
        expandScoringCriteriaValues:
          expandPromptsData?.generateExpandPrompts?.curatedSearch.scoringCriteria.map(
            (criterion) => criterion.criterion,
          ) ?? [],
        expandFilters:
          expandPromptsData?.generateExpandPrompts?.curatedSearch
            ?.internalCuratedSearch?.filters ?? null,
      });
      navigate("/autopilot/refresh-search-builder-for-ciro");
    } else if (
      expandPromptsData?.generateExpandPrompts?.curatedSearch
        ?.linkedinSalesNavigatorCuratedSearch
    ) {
      resetLsnHydrationValues({
        prompt:
          expandPromptsData?.generateExpandPrompts.curatedSearch.prompt ?? "",
        expandPrompt: selectedOption,
        scoringCriteriaValues:
          expandPromptsData?.generateExpandPrompts?.curatedSearch.scoringCriteria.map(
            (criterion) => criterion.criterion,
          ) ?? [],
        expandFilters:
          expandPromptsData?.generateExpandPrompts?.curatedSearch
            ?.linkedinSalesNavigatorCuratedSearch?.filters ?? null,
      });
      navigate("/autopilot/refresh-search-builder");
    }
  };

  return (
    <CiroModal isOpen={Boolean(curatedSearchId)} onClose={onClose}>
      <div className={classNames("text-lg", "font-semibold")}>
        Expand search
      </div>
      {expandPromptsLoading && (
        <div className={classNames("flex", "justify-center", "pt-4")}>
          <CiroSpinner loading />
        </div>
      )}
      {!expandPromptsLoading && (
        <div>
          <div className={classNames("text-sm", "text-neutral-500", "pt-4")}>
            Choose a search path to broaden your search:
          </div>
          <div className={classNames("flex", "flex-col", "gap-3", "pt-4")}>
            {expandPromptsData?.generateExpandPrompts?.expandPrompts.map(
              (option: string) => (
                <ExpandSearchModalOption
                  key={option}
                  description={option}
                  selected={selectedOption === option}
                  onClick={() => setSelectedOption(option)}
                />
              ),
            )}
            <div>
              <CiroTextArea
                placeholder="Create a custom suggestion"
                value={customExpandPrompt}
                onChange={(e) => {
                  setCustomExpandPrompt(e.target.value);
                  setSelectedOption(e.target.value);
                }}
                onFocus={() => setSelectedOption(customExpandPrompt)}
              />
            </div>
          </div>
          <div className={classNames("w-full", "pt-4")}>
            <CiroButton
              analyticsField="Continue search from expand modal"
              analyticsProps={{
                option: selectedOption,
              }}
              customClassName={classNames("w-full")}
              style={CiroButtonStyleEnum.LOUD}
              onClick={() => {
                if (selectedOption) {
                  handleSubmit(selectedOption.trim());
                } else {
                  setError("Please select an option");
                }
              }}
            >
              Confirm and continue search
            </CiroButton>
            <CiroErrorMsg error={error} />
          </div>
        </div>
      )}
    </CiroModal>
  );
};

export default AutopilotExpandSearchModal;
